import { useEffect, useState, useCallback } from "react";
import { getDependencies, addDependency, removeDependency } from "./DependenciesService";
import { getAllItems } from "./PlannerService";
import { useToast } from "../ToastContext";

const TYPE_LABELS = {
  DEPENDS_ON: "Depends on",
  BLOCKS: "Blocks",
  REQUIRES: "Requires",
  PRECEDES: "Precedes",
  FOLLOW_UP: "Follow-up of",
  RELATED_TO: "Related to",
  CONFLICTS_WITH: "Conflicts with",
};

/**
 * Add/view/remove generic dependencies for one planner item (Task or
 * Planner item — same table). Only rendered once the item exists (id
 * present), same rule CommentThread already follows.
 */
export default function DependenciesPanel({ itemId }) {
  const toast = useToast();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allItems, setAllItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [pickedId, setPickedId] = useState("");
  const [relType, setRelType] = useState("DEPENDS_ON");
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getDependencies(itemId)
      .then(setDetail)
      .catch(() => toast("Failed to load dependencies", "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getAllItems().then(items => setAllItems((items || []).filter(i => i.id !== itemId)));
  }, [itemId]);

  const handleAdd = async () => {
    if (!pickedId) return;
    setAdding(true);
    try {
      await addDependency(itemId, pickedId, relType);
      setPickedId("");
      setFilter("");
      load();
    } catch (err) {
      const msg = err?.status === 409 ? "That would create a cycle or already exists" : "Failed to add dependency";
      toast(msg, "error");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (dependencyId) => {
    try {
      await removeDependency(dependencyId);
      load();
    } catch {
      toast("Failed to remove dependency", "error");
    }
  };

  if (loading || !detail) return <div className="dep-panel">Loading dependencies…</div>;

  const filteredOptions = allItems.filter(i =>
    !filter.trim() || i.title?.toLowerCase().includes(filter.trim().toLowerCase())
  ).slice(0, 50);

  return (
    <div className="dep-panel">
      {detail.isBlocked && (
        <div className="dep-blocked-banner">
          ⛔ Blocked
          <ul>
            {detail.blockedByReasons.map((r, idx) => <li key={idx}>{r}</li>)}
          </ul>
        </div>
      )}

      {detail.downstreamBlockedCount > 0 && (
        <div className="dep-downstream-note">
          🔗 Delaying this affects {detail.downstreamBlockedCount} downstream item{detail.downstreamBlockedCount === 1 ? "" : "s"}.
        </div>
      )}

      <DepList label="This item" edges={detail.outgoing} onRemove={handleRemove} direction="from" />
      <DepList label="Pointing at this item" edges={detail.incoming} onRemove={handleRemove} direction="to" />

      <div className="dep-add-row">
        <input
          className="dep-add-filter"
          placeholder="Search item…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <select className="dep-add-select" value={pickedId} onChange={e => setPickedId(e.target.value)}>
          <option value="">Select item…</option>
          {filteredOptions.map(i => <option key={i.id} value={i.id}>{i.title}</option>)}
        </select>
        <select className="dep-add-type" value={relType} onChange={e => setRelType(e.target.value)}>
          {Object.entries(TYPE_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <button type="button" className="dep-add-btn" disabled={!pickedId || adding} onClick={handleAdd}>
          {adding ? "Adding…" : "+ Add"}
        </button>
      </div>
    </div>
  );
}

function DepList({ label, edges, onRemove, direction }) {
  if (!edges || edges.length === 0) return null;
  return (
    <div>
      <div className="dep-section-label">{label}</div>
      <div className="dep-list">
        {edges.map(e => (
          <div key={e.id} className="dep-row">
            <span className={`dep-type-badge ${e.hard ? "" : "dep-type-badge--soft"}`}>
              {TYPE_LABELS[e.relationshipType] || e.relationshipType}
            </span>
            <span className="dep-item-title">
              {direction === "from" ? e.toItemTitle : e.fromItemTitle}
            </span>
            <button type="button" className="dep-remove-btn" onClick={() => onRemove(e.id)}>✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}
